import { type ComponentType, lazy } from 'react';
import { matchPath } from 'react-router-dom';

type PageModule = { default: ComponentType };

export type LazyPage = ComponentType & { preload: () => Promise<void>; source: string };

/**
 * A code-split page that can also be loaded ahead of rendering. Once preloaded it
 * renders synchronously, which pre-built pages need: the server render and the
 * hydrating client render must not suspend.
 * `source` is the page's module path, used to preload its chunk from the pre-built HTML.
 */
export function lazyPage(load: () => Promise<PageModule>, source: string): LazyPage {
  let loaded: ComponentType | undefined;
  let pending: Promise<void> | undefined;
  const preload = () => (pending ??= load().then(module => { loaded = module.default; }));
  const Lazy = lazy(() => preload().then(() => ({ default: loaded! })));
  const Page = () => {
    const Component = loaded ?? Lazy;
    return <Component />;
  };
  return Object.assign(Page, { preload, source });
}

/** Finds the lazy page registered for a path (first match wins, like <Routes>). */
export function findLazyPage(routes: Array<[string, LazyPage | null]>, pathname: string) {
  const match = routes.find(([pattern]) => matchPath({ path: pattern, end: true }, pathname));
  return match?.[1] ?? null;
}
